// S3 Service for image uploads
import config from '../config';
import { API_ENDPOINTS } from '../config/apiRouting';

export interface UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

// For backward compatibility
export interface ImageUploadResult extends UploadResult {}

export interface ImageSet {
  menuImage?: string;
  onlineImage?: string;
  qualityControlImages: string[];
}

export class S3Service {
  private static readonly MENU_IMAGES_BUCKET = 'restaurant-ai-menu-images-prod';
  private static readonly MENU_VIDEOS_BUCKET = 'restaurant-ai-menu-videos-prod';

  /**
   * Upload a single image to S3
   */
  static async uploadImage(file: File, imageType: string, menuItemId: string): Promise<ImageUploadResult> {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '');
      const key = `menu-items/${menuItemId}/${imageType}-${timestamp}-${sanitizedFileName}`;

      // Convert file to base64
      const base64 = await this.fileToBase64(file);

      // Upload to our Lambda function
      let uploadUrl;
      try {
        uploadUrl = API_ENDPOINTS.uploadImage();
        console.log('S3Service: Generated upload URL:', uploadUrl);
      } catch (error) {
        console.error('S3Service: Error generating upload URL:', error);
        // Fallback to direct URL
        uploadUrl = 'https://sshble777esbdjwchwh72cz7be0lxwiy.lambda-url.us-east-1.on.aws/api/upload/image';
        console.log('S3Service: Using fallback URL:', uploadUrl);
      }
      
      if (!uploadUrl || uploadUrl.includes('undefined')) {
        throw new Error('Invalid upload URL generated');
      }
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file: base64,
          key: key,
          bucket: this.MENU_IMAGES_BUCKET,
          imageType: imageType,
          contentType: file.type,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      return {
        success: true,
        url: result.url,
        key: result.key,
      };
    } catch (error) {
      console.error('S3 upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  /**
   * Upload multiple images for a menu item
   */
  static async uploadImages(files: File[], imageType: string, menuItemId: string): Promise<ImageUploadResult[]> {
    const uploadPromises = files.map(file => this.uploadImage(file, imageType, menuItemId));
    return Promise.all(uploadPromises);
  }

  /**
   * Upload a video file to S3 for digital signage
   */
  static async uploadVideo(file: File, menuItemId: string): Promise<UploadResult> {
    try {
      // Validate video file type
      const allowedTypes = ['video/mp4', 'video/webm', 'video/avi', 'video/mov', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid video format. Please use MP4, WebM, AVI, or MOV files.');
      }

      // Create a unique filename
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '');
      const key = `menu-items/${menuItemId}/product-video-${timestamp}-${sanitizedFileName}`;

      // Convert file to base64
      const base64 = await this.fileToBase64(file);

      // Upload to our Lambda function
      let uploadUrl;
      try {
        uploadUrl = API_ENDPOINTS.uploadVideo();
        console.log('S3Service: Generated video upload URL:', uploadUrl);
      } catch (error) {
        console.error('S3Service: Error generating video upload URL:', error);
        // Fallback to direct URL
        uploadUrl = 'https://sshble777esbdjwchwh72cz7be0lxwiy.lambda-url.us-east-1.on.aws/api/upload/video';
        console.log('S3Service: Using fallback video URL:', uploadUrl);
      }
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file: base64,
          key: key,
          bucket: this.MENU_VIDEOS_BUCKET,
          contentType: file.type,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Video upload failed');
      }

      return {
        success: true,
        url: result.url,
        key: result.key,
      };
    } catch (error) {
      console.error('S3 video upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Video upload failed',
      };
    }
  }

  /**
   * Delete an image from S3
   */
  static async deleteImage(key: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.uploadImage(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, bucket: this.MENU_IMAGES_BUCKET }),
      });

      return response.ok;
    } catch (error) {
      console.error('S3 delete error:', error);
      return false;
    }
  }

  /**
   * Delete a video from S3
   */
  static async deleteVideo(key: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.uploadVideo(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, bucket: this.MENU_VIDEOS_BUCKET }),
      });

      return response.ok;
    } catch (error) {
      console.error('S3 video delete error:', error);
      return false;
    }
  }

  /**
   * Get presigned URL for image access
   */
  static async getPresignedUrl(key: string): Promise<string | null> {
    try {
      const response = await fetch(`${API_ENDPOINTS.uploadImage().replace('/api/upload/image', '/api/upload/presigned-url')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, bucket: this.MENU_IMAGES_BUCKET }),
      });

      const result = await response.json();
      return result.url || null;
    } catch (error) {
      console.error('Presigned URL error:', error);
      return null;
    }
  }

  /**
   * Convert file to base64 string
   */
  private static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Generate a temporary menu item ID for uploads before save
   */
  static generateTempMenuItemId(): string {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Move images from temp folder to permanent folder
   */
  static async moveImagesFromTemp(tempId: string, permanentId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_ENDPOINTS.uploadImage().replace('/api/upload/image', '/api/upload/move-images')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          tempId, 
          permanentId, 
          bucket: this.MENU_IMAGES_BUCKET 
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Move images error:', error);
      return false;
    }
  }
}

export default S3Service;